import { Box, Divider, Stack, styled, useTheme } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PoolTooltip, PoolTypography } from '@synergy/components-next';
import { ReactElement } from 'react';
import { useNavigate } from 'react-router';

import { ROUTES } from '../../../../utils/routes';
import { SupplierClassification } from '../../../../models/enums/supplier-classification';
import { SupplierListBoxBadges } from './supplier-list-item-box-badges';
import { SupplierListBoxIcons } from './supplier-list-item-box-icons';
import { SupplierListItemBoxContent } from './supplier-list-item-box-content';
import { SupplierListItemBoxProps } from './supplier-list-item-box-props';

const SupplierCard = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    transition: 'box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
        boxShadow: '0px 2px 14px 0px #21272E42',
        cursor: 'pointer',
    },
    [theme.breakpoints.up('sm')]: {
        height: 150,
    },
}));

const BoxContent = styled(Box, { shouldForwardProp: prop => prop !== 'isActive' })<{
    isActive?: boolean;
}>(({ theme, isActive }) => ({
    border: `2px solid ${isActive ? theme.palette.primary.main : theme.palette.grey[500]}`,
    borderRadius: '0 4px 4px 0',
    borderLeft: 'none',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    minWidth: 0,
    padding: theme.spacing(2),
    width: '100%',
}));

const BoxLabel = styled(Box, { shouldForwardProp: prop => prop !== 'isActive' })<{
    isActive?: boolean;
}>(({ theme, isActive }) => {
    const color = isActive ? theme.palette.primary.contrastText : theme.palette.grey[900];
    const background = isActive ? theme.palette.primary.main : theme.palette.grey[300];
    const borderColor = isActive ? theme.palette.primary.main : theme.palette.grey[500];

    return {
        border: `2px solid ${borderColor}`,
        borderLeft: 'none',
        borderRadius: '0 4px 4px 0',
        backgroundColor: background,
        color: color,
        width: theme.spacing(3),
        float: 'left',
        transform: 'rotate(180deg)',
        writingMode: 'vertical-rl',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };
});

const LeftBorderBox = (props: SupplierListItemBoxProps): ReactElement => {
    const isContractPartner = props.item.classification === SupplierClassification.Contract;

    return (
        <PoolTooltip
            mobileClickToOpen={true}
            preventPropagation={true}
            title={
                <FormattedMessage
                    id={
                        isContractPartner
                            ? 'app.supplierDirectory.card.contract.tooltip'
                            : 'app.supplierDirectory.card.bonus.tooltip'
                    }
                />
            }
        >
            <BoxLabel isActive={isContractPartner}>
                <PoolTypography
                    variant="statusLabel"
                    translationId={
                        isContractPartner ? 'app.supplierDirectory.card.contract' : 'app.supplierDirectory.card.bonus'
                    }
                />
            </BoxLabel>
        </PoolTooltip>
    );
};

export const SupplierListItemBoxCH = (props: SupplierListItemBoxProps): ReactElement => {
    const navigate = useNavigate();

    const handleClick = (): void => {
        navigate(ROUTES.supplierDirectory.detail.replace(':id', props.item.id.toString()));
    };

    const isContractPartner = props.item.classification === SupplierClassification.Contract;

    return (
        <SupplierCard>
            <LeftBorderBox item={props.item} />
            <BoxContent onClick={handleClick} isActive={isContractPartner}>
                <SupplierListBoxIcons item={props.item} />
                <SupplierListItemBoxContent item={props.item} />
                <Divider sx={{ my: 1.5 }} />
                <SupplierListBoxBadges item={props.item} />
            </BoxContent>
        </SupplierCard>
    );
};
